import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Container,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Snackbar,
  TextField,
  Typography
} from '@mui/material';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import { useAuth } from '../AuthContext';
import apiUrl from '../config';
import '../CSS/Login.css';

const LoginForm = () => {
  const [email, setEmail] = useState('');
  const [mot_de_passe, setMotDePasse] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [loadingLogin, setLoadingLogin] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, login } = useAuth();
  const from = location.state?.from?.pathname || '/';

  useEffect(() => {
    if (!loadingLogin && isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, loadingLogin, navigate, from]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const cleanEmail = email.trim().toLowerCase();

    if (!cleanEmail || !mot_de_passe) {
      setNotification({
        open: true,
        message: 'Veuillez renseigner votre email et votre mot de passe.',
        severity: 'error',
      });
      return;
    }

    setLoadingLogin(true);

    try {
      const res = await axios.post(`${apiUrl}/login`, {
        email: cleanEmail,
        mot_de_passe,
      });

      if (res.data.token) {
        login(res.data.role, res.data.token);
      } else {
        throw new Error('Token absent dans la réponse');
      }
    } catch (error) {
      console.error('Login error:', error.response ? error.response.data : error.message);
      setNotification({
        open: true,
        message: error.response?.data?.message || 'Email ou mot de passe incorrect.',
        severity: 'error',
      });
    } finally {
      setLoadingLogin(false);
    }
  };

  return (
    <main className="login-page">
      <Container maxWidth="lg" className="login-shell">
        <Paper elevation={0} className="login-card">
          <Box className="login-brand-panel">
            <div className="login-brand-content">
              <img src="/ethik-gestion-main-logo.png" alt="Logo ETHIK GESTION" className="login-logo" />
              <Typography className="login-eyebrow">Intranet sécurisé</Typography>
              <Typography variant="h3" component="h1" className="login-title">
                Gérez votre espace ETHIK GESTION simplement.
              </Typography>
              <Typography className="login-subtitle">
                Accédez à vos documents, déclarations, factures et informations personnelles depuis un espace clair et sécurisé.
              </Typography>

              <div className="login-security-box">
                <SecurityRoundedIcon />
                <span>Connexion protégée par authentification sécurisée</span>
              </div>
            </div>
          </Box>

          <Box className="login-form-panel">
            <div className="login-form-heading">
              <Typography variant="h4" component="h2" className="login-form-title">
                Connexion
              </Typography>
              <Typography className="login-form-subtitle">
                Entrez vos identifiants pour continuer.
              </Typography>
            </div>

            <form onSubmit={handleSubmit} className="login-form" noValidate>
              <TextField
                fullWidth
                label="Adresse email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailRoundedIcon />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Mot de passe"
                type={showPassword ? 'text' : 'password'}
                value={mot_de_passe}
                onChange={(e) => setMotDePasse(e.target.value)}
                required
                autoComplete="current-password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockRoundedIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                        onClick={() => setShowPassword((value) => !value)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box className="login-options-row">
                <span className="login-help-text">Besoin d’aide ?</span>
                <Link component={RouterLink} to="/mot-de-passe-oublie" className="login-forgot-link">
                  Mot de passe oublié
                </Link>
              </Box>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                className="login-submit"
                disabled={loadingLogin}
                endIcon={!loadingLogin ? <ArrowForwardRoundedIcon /> : null}
              >
                {loadingLogin ? 'Connexion en cours...' : 'Se connecter'}
              </Button>
            </form>
          </Box>
        </Paper>
      </Container>

      <Snackbar
        open={notification.open}
        autoHideDuration={3500}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={notification.severity} sx={{ width: '100%', fontWeight: 700 }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </main>
  );
};

export default LoginForm;
